import moment from 'moment'

import { getCardCost } from '../../../constants/VisitsConstants'
import {
  calculateCashItemsDetails,
  calculateInsuranceItemsDetails,
  getInsuranceRemainingLimit
} from './VisitItemsCalculations'

const isDepositPaymentMethod = paymentMethod =>
  paymentMethod?.toLowerCase() === 'deposit'

const withoutVatForDeposit = values =>
  isDepositPaymentMethod(values?.paymentMethod)
    ? {
      ...values,
      items: values?.items?.map(item => ({
        ...item,
        tax_check: false,
        payer_vat: false
      }))
    }
    : values

const buildPaymentDetails = (values, cardOptions, totalAmount) => {
  if (values?.paymentMethod === 'multi_payment') {
    return [
      ...(Number(values?.cash) > 0
        ? [{ payment_method: 'cash', amount: values.cash }]
        : []),
      ...(Number(values?.bank) > 0
        ? [{ payment_method: 'bank', amount: values.bank }]
        : []),
      ...(Number(values?.credit) > 0
        ? [{ payment_method: 'credit', amount: values.credit }]
        : []),
      ...(values.cards
        ?.filter(card => Number(card.amount) > 0)
        ?.map(card => ({
          payment_method: card.cardType,
          amount: card.amount,
          card_cost: getCardCost(cardOptions, card.cardType, card.amount)
        })) || [])
    ]
  }

  if (values?.paymentMethod === 'card') {
    return [
      {
        payment_method: values?.cardType,
        amount: totalAmount,
        card_cost: getCardCost(cardOptions, values?.cardType, totalAmount)
      }
    ]
  }

  return undefined
}

const buildUnitPaymentRequest = ({
  t,
  values,
  unitItems,
  formattedVisitDate,
  cardCostValue,
  cardOptions,
  selectedPatientData,
  selectedPayerData,
  insuranceLimit,
  billingVisitData,
  currentInvoiceDate
}) => {
  const unitValues = { ...values, items: unitItems }
  const payloadValues = withoutVatForDeposit(unitValues)

  const itemsDetails =
    values?.patientType === 'cash'
      ? calculateCashItemsDetails(
        {
          ...payloadValues,
          document_type: selectedPatientData?.document_type
        },
        cardCostValue
      )
      : calculateInsuranceItemsDetails(
        payloadValues,
        insuranceLimit ?? getInsuranceRemainingLimit(selectedPayerData),
        selectedPatientData,
        cardCostValue
      )

  const cashDetails = calculateCashItemsDetails({
    ...payloadValues,
    document_type: selectedPatientData?.document_type
  })

  const insuranceDetails = calculateInsuranceItemsDetails(
    payloadValues,
    insuranceLimit ?? getInsuranceRemainingLimit(selectedPayerData),
    selectedPatientData
  )

  const netCashPay = Number(
    (cashDetails?.totals?.patientShareTotal || 0) +
    (cashDetails?.totals?.patientTaxTotal || 0)
  )

  const netInsurancePay = Number(
    (insuranceDetails?.totals?.patientShareTotal || 0) +
    (insuranceDetails?.totals?.patientTaxTotal || 0)
  )

  const totalAmount =
    values?.patientType !== 'cash' ? netInsurancePay : netCashPay

  const items = payloadValues?.items
    .map(item => ({
      ...item,
      approved_amount: Number(item?.approved_amount) || 0,
      discount_amount: Number(item?.discount_amount),
      quantity: Number(item?.quantity),
      unit_price: Number(item?.unit_price),
      patientShareAmount: Number(item?.patient_share),
      patient_share: String(item?.patient_share),
      status: item?.checked && values?.paid === 'paid' ? 'Paid' : 'UnPaid',
      ...(item?.is_approval && {
        approved_qty: Number(item?.approved_qty) || 0,
        approved_amount: Number(item?.approved_amount) || 0
      })
    }))
    .map(
      ({ low_stock, out_of_stock, is_stock, stock, manage_costs, ...item }) =>
        item
    )

  const paymentDetails = buildPaymentDetails(values, cardOptions, totalAmount)

  const invoicePayload = {
    date: currentInvoiceDate,
    paid: values?.paid === 'paid',
    invoice_ref: 0,
    description: '',
    item_details: itemsDetails,
    customer_id: selectedPatientData?.patient_id,
    payment_method: values?.paymentMethod,
    ...(paymentDetails && { payment_detail: paymentDetails }),
    tax_type: 'percentage',
    tax_amount: 15,
    ...(values?.patientType === 'cash'
      ? { invoice_type: 'sales', payer_id: selectedPayerData?.payer_id }
      : {
        invoice_type: 'credit',
        payer_id: selectedPayerData?.his_payer_id,
        selected_plan: selectedPayerData
      })
  }

  const billingPayload = {
    visit_id: billingVisitData?.visit_id,
    payment_status: 'Paid',
    items: items.filter(item => item?.checked),
    invoice_details: invoicePayload
  }

  const creditPayload = {
    date: moment(values?.visit_date).format('YYYY-MM-DD'),
    patient_name: selectedPatientData?.full_name,
    patient_id: selectedPatientData?.patient_id,
    patient_document_id: selectedPatientData?.document_id,
    patient_file_no: selectedPatientData?.file_no,
    department_id: billingVisitData?.practitioner?.department?.id,
    department_name: billingVisitData?.practitioner?.department?.name_en,
    patient_credit: {
      patient_credit_amount:
        values?.paymentMethod === 'credit'
          ? Number(totalAmount)
          : values?.paymentMethod === 'multi_payment' &&
            Number(values?.credit) > 0
            ? Number(values?.credit)
            : 0,
      remarks: values?.comment || t('Credit Added')
    }
  }

  return { billingPayload, creditPayload, totalAmount, netCashPay }
}

export default buildUnitPaymentRequest
